import sprintfDefault from 'sprintf-js';

import { stripCredentials, stripSecretsFromText } from 'utils/stripCredentials';

const { sprintf } = sprintfDefault;

type ConsoleMethod = (...data: unknown[]) => void;

// These lines go out with feedback reports. Through JSON first, because that's how Errors and Records show their fields.
const toLogValue = (param: unknown): unknown => {
  if (typeof param !== 'object') return stripCredentials(param);
  const json = JSON.stringify(param);
  return json === undefined ? json : JSON.stringify(stripCredentials(JSON.parse(json)), null, 2);
};

class ConsoleMirror {
  #superConsole: Console | undefined;

  #maxLogLength = 256;

  #maxMessageLength = 4 * 1024 * 1024; // 4Mb

  #log: string[] = [];

  attach(): void {
    this.#superConsole = console;
    // biome-ignore lint/suspicious/noConsole: this module wraps the console
    const superLog = console.log;
    console.log = (...rest) => this.applyLog(superLog, rest, '[log] ');
    // biome-ignore lint/suspicious/noConsole: this module wraps the console
    const superInfo = console.info;
    console.info = (...rest) => this.applyLog(superInfo, rest, '[info] ');
    // biome-ignore lint/suspicious/noConsole: this module wraps the console
    const superError = console.error;
    console.error = (...rest) => this.applyLog(superError, rest, '[error] ');
    // biome-ignore lint/suspicious/noConsole: this module wraps the console
    const superWarn = console.warn;
    console.warn = (...rest) => this.applyLog(superWarn, rest, '[warn] ');
    // biome-ignore lint/suspicious/noConsole: this module wraps the console
    const superDebug = console.debug;
    console.debug = (...rest) => this.applyLog(superDebug, rest, '[debug] ');
  }

  applyLog(superMethod: ConsoleMethod, parametersRaw: unknown[], tag = ''): void {
    superMethod.apply(this.#superConsole, parametersRaw);
    try {
      let message = '';
      let format: string | undefined;
      if (parametersRaw.length > 1 && typeof parametersRaw[0] === 'string') {
        // might be substitutions string
        format = (parametersRaw.shift() as string)
          .replace('%c', '%.0s') // drop %c on the floor
          .replace('%o', '%s') // printf doesn't support %o
          .replace('%O', '%s'); // printf doesn't support %O
      }
      const parameters = parametersRaw.map(toLogValue);
      if (format) {
        message += sprintf(format, ...parameters);
        const capturedNum = format.match(/%\.?\d*[dsif]/g)?.length ?? 0;
        parameters.splice(0, capturedNum);
      }
      message += parameters.length ? ' ' : '';
      message += parameters.join(' ');
      message = stripSecretsFromText(message);
      if (
        !assert(message.length < this.#maxMessageLength, `maximum message length ${this.#maxMessageLength} exceeded`)
      ) {
        message = message.substring(0, this.#maxMessageLength).concat('...');
      }
      const date = new Date().toISOString();
      this.#log.push(`${tag}${date}: ${message}`);
      if (this.#log.length > this.#maxLogLength) {
        this.#log.splice(0, this.#log.length - this.#maxLogLength);
      }
    } catch (error) {
      verify(false, error);
    }
  }

  /** A copy of the mirrored lines, oldest first; callers may sort or reverse it. */
  get log(): string[] {
    return [...this.#log];
  }
}

const consoleMirror = new ConsoleMirror();
export default consoleMirror;
